import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { BrandIcon, type BrandIconName } from '../../Landing';
import { t } from '../../i18n';

export type Tab = { to: string; label: string; icon: BrandIconName };

/**
 * Both shells drew their own navigation — the same NavLink, the same
 * `bottom-nav-item`, the same truncating label — once for the owner and once
 * for staff, top row and bottom bar, four copies of one bar. The only real
 * difference is the round button: the owner has one thing he does every
 * morning, and staff do not.
 *
 * The `rounded-md` that used to sit on the icons is gone. It was shaping the
 * corners of a 3D PNG; the glyphs are stroked paths with no fill, so it had
 * been styling nothing since the icons changed.
 */
export function DesktopTabs({
  tabs,
  className = 'app-container',
  trailing,
}: {
  tabs: Tab[];
  /** The staff shell narrows its column per route, so it passes its own. */
  className?: string;
  trailing?: ReactNode;
}) {
  return (
    <nav className="hidden border-b border-border bg-white sm:block">
      <div className={`${className} flex gap-1 overflow-x-auto`}>
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              `flex items-center gap-2 px-3.5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                isActive
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`
            }
          >
            <BrandIcon name={tab.icon} className="h-6 w-6" />
            {tab.label}
          </NavLink>
        ))}
        {trailing}
      </div>
    </nav>
  );
}

export function BottomNav({
  tabs,
  center,
}: {
  tabs: Tab[];
  /** The round button. Given one, the bar splits 2 · hole · rest around it. */
  center?: { label: string; onClick: () => void };
}) {
  const before = center ? tabs.slice(0, 2) : tabs;
  const after = center ? tabs.slice(2) : [];

  return (
    <nav className="bottom-nav" aria-label={t('Primary')}>
      {before.map((tab) => (
        <BottomTab key={tab.to} tab={tab} />
      ))}

      {center && (
        <>
          {/* The hole the round button sits in. */}
          <div className="bottom-nav-item pointer-events-none opacity-0" aria-hidden="true">
            <span className="bottom-nav-icon" />
            <span>·</span>
          </div>
          {after.map((tab) => (
            <BottomTab key={tab.to} tab={tab} />
          ))}
          <button
            type="button"
            onClick={center.onClick}
            aria-label={center.label}
            className="absolute -top-6 left-1/2 flex h-14 w-14 -translate-x-1/2 items-center justify-center rounded-full border-4 border-background bg-primary text-3xl font-light text-white transition-transform duration-150 active:scale-95"
          >
            +
          </button>
        </>
      )}
    </nav>
  );
}

function BottomTab({ tab }: { tab: Tab }) {
  return (
    <NavLink
      to={tab.to}
      end
      className={({ isActive }) => `bottom-nav-item ${isActive ? 'text-foreground' : ''}`}
    >
      <BrandIcon name={tab.icon} className="bottom-nav-icon" />
      <span className="truncate px-1">{tab.label}</span>
    </NavLink>
  );
}
